/**
 * Node Operations Behavior
 *
 * Handles node creation and deletion:
 * - Delete/Backspace to delete selected
 * - Double-click to create node
 */

import type { NodeOpsBehaviorContext } from "./types";
import * as viewState from "../view-state";
import type { ViewState } from "../types";

/**
 * Register node operations behavior handlers.
 * Returns cleanup function to unregister all handlers.
 */
export function registerNodeOpsBehavior(
  ctx: NodeOpsBehaviorContext,
): () => void {
  const {
    input,
    getViewState,
    updateViewState,
    hitTest,
    getNodes,
    getEdges,
    screenToWorld,
    createNode,
    deleteNodes,
    deleteEdges,
    editNode,
    stopEditingNode,
    log,
  } = ctx;
  const unsubscribers: (() => void)[] = [];

  // =========================================================================
  // Delete Selected
  // =========================================================================

  function deleteSelected(): void {
    const state = getViewState();
    const selected = viewState.getSelectedIds(state);
    if (selected.length === 0) return;

    const deletedIds = new Set(selected);

    // Separate nodes and edges
    const nodeIds = new Set(getNodes().map((n) => n.id));
    const edgeIds = new Set(getEdges().map((e) => e.id));

    const deletedNodeIds = selected.filter((id) => nodeIds.has(id));
    const deletedEdgeIds = selected.filter((id) => edgeIds.has(id));

    // Delete nodes and edges
    if (deletedNodeIds.length > 0) {
      deleteNodes(deletedNodeIds);
    }
    if (deletedEdgeIds.length > 0) {
      deleteEdges(deletedEdgeIds);
    }

    // Clear selection
    updateViewState(viewState.clearSelection(state));

    // Log what was deleted
    const parts: string[] = [];
    if (deletedNodeIds.length > 0) {
      parts.push(`${deletedNodeIds.length} nodes`);
    }
    if (deletedEdgeIds.length > 0) {
      parts.push(`${deletedEdgeIds.length} edges`);
    }
    log?.("delete", parts.join(", ") + ": " + selected.join(", "));
  }

  unsubscribers.push(
    input.on(
      "keydown",
      "delete",
      {
        mode: "default",
        excludeModes: ["editing"],
        command: { id: "edit.delete", name: "Delete" },
        category: "Edit",
      },
      () => {
        deleteSelected();
      },
    ),
  );

  unsubscribers.push(
    input.on(
      "keydown",
      "backspace",
      {
        mode: "default",
        excludeModes: ["editing"],
        command: { id: "edit.delete-backspace", name: "Delete" },
        category: "Edit",
      },
      () => {
        deleteSelected();
      },
    ),
  );

  // =========================================================================
  // Double-Click to Create/Edit
  // =========================================================================

  unsubscribers.push(
    input.on("dblclick", { mode: "default" }, (inputCtx) => {
      const point = screenToWorld(inputCtx.position!.x, inputCtx.position!.y);
      const hit = hitTest(point);

      if (!hit.nodeId) {
        // Clicking on empty space - create new node
        createNode("text", point);
      } else if (editNode) {
        // Start editing via plugin system (handles UI + view state)
        editNode(hit.nodeId);
      } else {
        // Fallback: just set view state (no plugin system available)
        const state = getViewState();
        updateViewState(viewState.startEditing(state, hit.nodeId, "content"));
        log?.("edit-start", `node: ${hit.nodeId}`);
      }
    }),
  );

  // =========================================================================
  // Exit Editing: Escape Key
  // =========================================================================

  unsubscribers.push(
    input.on(
      "keydown",
      "escape",
      {
        mode: "*", // Listen in all modes to catch editing state
        excludeModes: ["editing"], // Rich editor handles its own Escape
      },
      () => {
        const state = getViewState();
        if (viewState.isEditing(state)) {
          updateViewState(viewState.stopEditing(state));
          log?.("edit-cancel", "escape pressed");
        }
      },
    ),
  );

  // =========================================================================
  // Exit Editing: Click Outside
  // =========================================================================

  unsubscribers.push(
    input.on("mousedown", { mode: "*" }, (inputCtx) => {
      const state = getViewState();
      if (!viewState.isEditing(state)) return;

      const point = screenToWorld(inputCtx.position!.x, inputCtx.position!.y);
      const hit = hitTest(point);

      // If clicking outside the currently editing node, stop editing
      // and update selection (since selection handlers use mode:'default'
      // and won't fire while in 'editing' mode)
      if (hit.nodeId !== state.editing?.nodeId) {
        // Stop editing plugin (saves content)
        if (stopEditingNode) {
          stopEditingNode(true);
        }

        // Update view state: stop editing + update selection
        let newState = viewState.stopEditing(getViewState());
        if (hit.nodeId) {
          // Clicked a different node - select it
          newState = viewState.applySelection(newState, hit.nodeId, "replace");
        } else if (!hit.edgeId) {
          // Clicked empty space - clear selection
          newState = viewState.clearSelection(newState);
        }
        updateViewState(newState);
        log?.("edit-stop", `clicked outside: ${hit.nodeId ?? "empty"}`);
      }
    }),
  );

  // =========================================================================
  // Cleanup
  // =========================================================================

  return () => {
    for (const unsub of unsubscribers) {
      unsub();
    }
  };
}
